// Creative asset preview resolution helpers.
//
// Extracted from CreativeTrafficking so the preview-source contract can be unit
// tested in isolation (the page itself pulls in router/CSS/asset imports that a
// plain unit test can't resolve). Pure functions only — no React, no network.

const normalizeBase = (base) => {
  if (!base) return "";
  let next = String(base).trim();
  next = next.replace(/\/+$/, "");
  if (next.toLowerCase().endsWith("/api")) next = next.slice(0, -4);
  return next;
};

export const API_BASE_URL =
  normalizeBase(process.env.REACT_APP_BACKEND_URL) ||
  normalizeBase(process.env.REACT_APP_BACKEND) ||
  normalizeBase(process.env.REACT_APP_API_URL) ||
  "http://localhost:5000";

// Aspect ratio (width / height) from known pixel dimensions, or null when the
// dimensions are missing/invalid. Used to shape the preview frame so a creative
// is letterboxed/pillarboxed (object-fit: contain) at its true proportions
// instead of being cropped into a square.
export function getCreativeAspectRatio(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return w / h;
}

// Single source-of-truth for the preview `sourceUrl` fed to CreativePreviewTile.
// Used by the row thumbnail, the inline detail, AND the expanded modal so they
// always resolve the SAME preview source. Prefers the (row-proven) thumbnail
// source, then the asset URL for image assets. Videos with no thumbnail fall
// back to the tile's authenticated stream path (fallbackApiPath).
export function resolveCreativePreviewSourceUrl(row = {}) {
  const thumbnailUrl = row?.thumbnailUrl || "";
  const assetUrl = row?.assetUrl || "";
  const isImage = String(row?.previewMimeType || "").startsWith("image/");
  return thumbnailUrl || (isImage ? assetUrl : "");
}

export function inferCreativeType(asset) {
  const mimeType = String(asset?.mime_type || "").toLowerCase();
  const fileName = String(asset?.file_name || "").toLowerCase();

  if (mimeType.startsWith("video/")) return "Video";
  if (mimeType.startsWith("image/")) return "Image";
  if ([".mp4", ".mov", ".webm", ".m4v", ".avi"].some((ext) => fileName.endsWith(ext))) {
    return "Video";
  }
  return "Asset";
}

export function getCreativeMimeType(asset) {
  const explicitMimeType = String(
    asset?.mime_type ||
      asset?.approvedVersion?.mime_type ||
      asset?.currentVersion?.mime_type ||
      asset?.metadata?.mime_type ||
      ""
  ).toLowerCase();
  if (explicitMimeType) return explicitMimeType;

  const candidateText = [
    asset?.file_name,
    asset?.approvedVersion?.file_name,
    asset?.currentVersion?.file_name,
    asset?.google_drive_file_url,
    asset?.dropbox_url,
    asset?.media_url,
    asset?.mediaUrl,
    asset?.stream_url,
    asset?.streamUrl,
    asset?.signed_url,
    asset?.signedUrl,
    asset?.file_url,
    asset?.fileUrl,
    asset?.preview_url,
    asset?.previewUrl,
    asset?.thumbnail_url,
    asset?.thumbnailUrl,
    asset?.poster_url,
    asset?.posterUrl,
    asset?.url,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  if (
    candidateText.some((value) => /\.(mp4|mov|webm|m4v|avi)(\?|#|$)/i.test(value)) ||
    candidateText.some((value) => value.includes("/stream")) ||
    candidateText.some((value) => value.includes("video"))
  ) {
    return "video/mp4";
  }
  if (candidateText.some((value) => /\.(png|jpe?g|webp|gif|avif|svg)(\?|#|$)/i.test(value))) {
    return "image/png";
  }
  return "";
}

export function getCreativeThumbnailUrl(asset) {
  const mimeType = getCreativeMimeType(asset);
  const explicitThumbnail =
    asset?.thumbnail_url ||
    asset?.poster_url ||
    asset?.posterUrl ||
    asset?.thumbnailUrl ||
    "";
  if (explicitThumbnail) return String(explicitThumbnail);

  const previewUrl = asset?.preview_url || asset?.previewUrl || "";
  if (!previewUrl) return "";

  const previewKind = inferResolvedPreviewKind(previewUrl, mimeType, true);
  return previewKind === "image" ? String(previewUrl) : "";
}

export function getCreativeAssetStreamPaths(asset) {
  const candidates = [
    asset?.currentVersion?.id,
    asset?.current_version_id,
    asset?.approvedVersion?.id,
    asset?.approved_version_id,
    asset?.id,
  ]
    .map((value) => {
      const normalized = Number(value);
      return Number.isFinite(normalized) ? normalized : null;
    })
    .filter(Boolean);

  const seen = new Set();
  const paths = [];

  candidates.forEach((candidateId) => {
    const versionPath = `/api/creative-assets/versions/${candidateId}/stream`;
    if (!seen.has(versionPath)) {
      seen.add(versionPath);
      paths.push(versionPath);
    }
  });

  const assetId = Number(asset?.id);
  if (Number.isFinite(assetId)) {
    const assetPath = `/api/creative-assets/${assetId}/stream`;
    if (!seen.has(assetPath)) {
      seen.add(assetPath);
      paths.push(assetPath);
    }
  }

  return paths;
}

export function getCreativeAssetUrl(asset) {
  const mimeType = getCreativeMimeType(asset);
  const apiPath = getCreativeAssetStreamPaths(asset)[0] || "";
  if (mimeType.startsWith("video/") && apiPath) {
    return `${API_BASE_URL}${apiPath}`;
  }
  const directUrl =
    asset?.google_drive_file_url ||
    asset?.dropbox_url ||
    asset?.media_url ||
    asset?.mediaUrl ||
    asset?.stream_url ||
    asset?.streamUrl ||
    asset?.signed_url ||
    asset?.signedUrl ||
    asset?.file_url ||
    asset?.fileUrl ||
    asset?.url ||
    "";
  if (directUrl) return String(directUrl);
  const previewUrl = asset?.preview_url || asset?.previewUrl || "";
  if (previewUrl && !mimeType.startsWith("video/")) return String(previewUrl);
  return apiPath ? `${API_BASE_URL}${apiPath}` : "";
}

export function inferResolvedPreviewKind(url, mimeType, thumbnailMode = false) {
  const raw = String(url || "").trim().toLowerCase();
  if (!raw) {
    return String(mimeType || "").startsWith("video/") ? "video" : "image";
  }
  if (raw.startsWith("data:image/")) return "image";
  if (raw.startsWith("data:video/")) return "video";
  if (/\.(png|jpe?g|webp|gif|avif|svg)(\?|#|$)/i.test(raw)) return "image";
  // The governed review poster (/api/creative-assets/:id/poster) is a JPEG
  // still of a video creative: it must render as an image even though the
  // creative's own mime type is video/*, or the tile would try to play it.
  if (/\/poster(\?|#|$)/i.test(raw)) return "image";
  if (/\.(mp4|mov|webm|m4v|avi)(\?|#|$)/i.test(raw)) return "video";
  // An explicit image mime always wins. Otherwise a successfully-fetched image
  // blob (blob:...) or an image served via the /stream proxy would be wrongly
  // treated as video by the heuristics below and rendered in a <video> element.
  if (String(mimeType || "").startsWith("image/")) return "image";
  if (
    thumbnailMode &&
    (String(mimeType || "").startsWith("video/") ||
      raw.startsWith("blob:") ||
      raw.includes("/stream"))
  ) {
    return "video";
  }
  if (thumbnailMode) return "image";
  return String(mimeType || "").startsWith("video/") ? "video" : "image";
}
